import { type ReactNode, useRef, useCallback, useEffect } from 'react';
import styles from './Sidebar.module.css';

interface SidebarProps {
  children: ReactNode;
  header?: ReactNode;
  footer?: ReactNode;
  defaultWidth?: number;
  minWidth?: number;
  maxWidth?: number;
  mobileDrawerOpen: boolean;
  onClose?: () => void;
}

function setSidebarVar(px: number) {
  document.documentElement.style.setProperty('--sidebar-width', px + 'px');
}

export function Sidebar({
  children,
  header,
  footer,
  defaultWidth = 240,
  minWidth = 180,
  maxWidth = 400,
  mobileDrawerOpen,
  onClose,
}: SidebarProps) {
  const asideRef = useRef<HTMLElement>(null);
  // Where focus came from before the mobile drawer captured it, to restore on close.
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // The CSS var is the single source of truth for sidebar width: the grid track
  // (layout.css) and the aside both read it, so drag updates only ever write here.
  useEffect(() => { setSidebarVar(defaultWidth); }, [defaultWidth]);

  const onMouseDown = useCallback(() => {
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMouseMove = (e: MouseEvent) => {
      setSidebarVar(Math.min(maxWidth, Math.max(minWidth, e.clientX)));
    };

    const onMouseUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [minWidth, maxWidth]);

  // Mobile overlay only: trap nothing on the persistent tablet/desktop sidebar,
  // but when the drawer is open on a phone, move focus in, restore it on close,
  // and close on Escape.
  useEffect(() => {
    if (!mobileDrawerOpen) return;
    restoreFocusRef.current = document.activeElement as HTMLElement | null;
    asideRef.current?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current?.();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      restoreFocusRef.current?.focus();
    };
  }, [mobileDrawerOpen]);

  return (
    <aside
      ref={asideRef}
      className={styles.sidebar}
      data-open={mobileDrawerOpen}
      role={mobileDrawerOpen ? 'dialog' : undefined}
      aria-modal={mobileDrawerOpen ? true : undefined}
      aria-label={mobileDrawerOpen ? 'Navigation' : undefined}
      tabIndex={mobileDrawerOpen ? -1 : undefined}
    >
      <div className={styles.body}>
        {header && <div className={styles.header}>{header}</div>}
        <div className={styles.content}>{children}</div>
        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
      <div className={styles.resizeHandle} onMouseDown={onMouseDown} />
    </aside>
  );
}
